import { RadioANTD } from '@zennolab_platform/webui-kit/dist/ui/antdComponents';
import { NotificationPreview } from '@zennolab_platform/webui-sdk/dist/components/NotificationCenter';
import { useFormatMessage } from '@zennolab_platform/webui-sdk/dist/hooks';
import { EAnnouncementType } from '@zennolab_platform/webui-sdk/dist/services/NotificationService';
import { ELocale } from '@zennolab_platform/webui-sdk/dist/stores/IntlStore';
import { useState } from 'react';

import { LANGUAGES } from '../../constants/announcement';
import { AnnouncementType, type IAnnouncementLocalization } from '../../types/announcement';
import { pickLocalization } from '../../utils/announcement';
import { ImportantNote, PreviewFrame, PreviewSection } from './styled';

type Language = (typeof LANGUAGES)[number];

const LOCALES: Record<Language, ELocale> = { ru: ELocale.RU, en: ELocale.EN, zh: ELocale.ZH };

const SDK_TYPES: Partial<Record<AnnouncementType, EAnnouncementType>> = {
  [AnnouncementType.ANNOUNCEMENT_TYPE_UPDATE]: EAnnouncementType.Update,
  [AnnouncementType.ANNOUNCEMENT_TYPE_PROMO]: EAnnouncementType.Promo,
  [AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE]: EAnnouncementType.Maintenance,
  [AnnouncementType.ANNOUNCEMENT_TYPE_NEWS]: EAnnouncementType.News,
};

/** The item as ZennoBrowser's Updates tab will draw it, from the form's current state (webui-sdk NotificationPreview). */
export function PreviewPanel({
  localizations,
  type,
  businessVersion,
  pinned,
  important,
}: {
  localizations: IAnnouncementLocalization[];
  type: AnnouncementType;
  /** Set (possibly empty) for a version card. */
  businessVersion?: string;
  pinned?: boolean;
  important?: boolean;
}) {
  const { t } = useFormatMessage();
  const [language, setLanguage] = useState<Language>(() => {
    const initial = localizations.find(l => l.isDefault)?.language;
    return LANGUAGES.find(lang => lang === initial) ?? 'ru';
  });
  const shown = pickLocalization(localizations, language);
  const title = t({
    id: 'announcements.preview.title',
    defaultMessage: 'Предпросмотр',
    description: 'Заголовок блока предпросмотра',
  });

  return (
    <PreviewSection aria-label={title}>
      <h2>{title}</h2>
      <RadioANTD.Group
        optionType="button"
        aria-label={t({
          id: 'announcements.preview.language',
          defaultMessage: 'Язык предпросмотра',
          description: 'Подпись переключателя языка предпросмотра',
        })}
        value={language}
        options={LANGUAGES.map(lang => ({ label: lang.toUpperCase(), value: lang }))}
        onChange={e => setLanguage(e.target.value as Language)}
      />
      <PreviewFrame>
        <NotificationPreview
          kind={businessVersion !== undefined ? 'versionCard' : 'announcement'}
          title={shown?.title ?? ''}
          text={shown?.text ?? ''}
          ctaLabel={shown?.ctaLabel}
          ctaUrl={shown?.ctaUrl}
          announcementType={SDK_TYPES[type]}
          businessVersion={businessVersion}
          pinned={pinned}
          important={important}
          locale={LOCALES[language]}
        />
      </PreviewFrame>
      {/* Admin UI, so in the admin language whatever language the preview shows. */}
      {important && (
        <ImportantNote data-testid="PreviewImportantNote">
          {t({
            id: 'announcements.preview.importantNote',
            defaultMessage: 'Важное: появится и во всплывающем уведомлении',
            description: 'Пометка под предпросмотром важного анонса',
          })}
        </ImportantNote>
      )}
    </PreviewSection>
  );
}
